import { signal } from '@angular/core';
import Echo from 'laravel-echo';
import Pusher from 'pusher-js';

export type EchoChannelType = 'channel' | 'join';

export interface EchoListenConfig {
  event: string;
  callback: (data: any) => void;
}

export interface EchoWhisperConfig {
  event: string;
  callback: (data: any) => void;
}

export interface EchoChannelConfig {
  type: EchoChannelType;
  channel: string;
  listen?: EchoListenConfig[];
  listenForWhisper?: EchoWhisperConfig[];
  onSubscribed?: () => void;
  onError?: (error: any) => void;
}

export interface EchoConnectionConfig {
  broadcaster: string;
  key: string;
  wsHost: string;
  wsPort: number;
  wssPort: number;
  forceTLS: boolean;
  enabledTransports: string[];
  authEndpoint: string;
  auth: {
    headers: {
      Authorization: string;
      Accept: string;
    };
  };
  [key: string]: any;
}

export interface EchoConnectionCallbacks {
  onConnected?: () => void;
  onConnecting?: () => void;
  onDisconnected?: () => void;
  onError?: (error: any) => void;
  onFailed?: () => void;
  onUnavailable?: () => void;
  onBackOnline?: () => void;
}

export class EchoManager {
  private echo: Echo<any> | null = null;
  private context: string = '';
  private channels: Map<string, any> = new Map();

  socketsStatus = signal<'online' | 'offline' | 'backOnline'>('online')
  constructor(context: string = '') {
    this.context = context;
  }

  /**
   * Initialize Echo instance with configuration
   */
  init(config: EchoConnectionConfig): void {
    (window as any).Pusher = Pusher;

    this.echo = new Echo(config as any);
    console.log(`[${this.context}] Echo initialized`);
  }

  /**
   * Setup connection status listeners
   */
  setupConnectionStatus(callbacks: EchoConnectionCallbacks = {}): void {
    if (!this.echo) {
      console.warn(`[${this.context}] Echo not initialized`);
      return;
    }

    const pusher = this.echo.connector?.pusher;
    const prefix = this.context ? `[${this.context}]` : '';

    if (!pusher?.connection) return;

    pusher.connection.bind('connected', () => {
      try {
        console.log(`${prefix} WebSocket connected successfully`);
        if (this.socketsStatus() === 'offline') {
          this.socketsStatus.set('backOnline');
          callbacks.onBackOnline?.();
          setTimeout(() => this.socketsStatus.set('online'), 8000);
        }
        callbacks.onConnected?.();
      } catch (error) {
        console.error(`${prefix} Error in connected callback:`, error);
      }
    });

    pusher.connection.bind('connecting', () => {
      try {
        console.log(`${prefix} WebSocket connecting...`);
        callbacks.onConnecting?.();
      } catch (error) {
        console.error(`${prefix} Error in connecting callback:`, error);
      }
    });

    pusher.connection.bind('disconnected', () => {
      try {
        console.log(`${prefix} WebSocket disconnected`);
        this.socketsStatus.set('offline')
        callbacks.onDisconnected?.();
      } catch (error) {
        console.error(`${prefix} Error in disconnected callback:`, error);
      }
    });

    pusher.connection.bind('error', (err: any) => {
      try {
        console.warn(`${prefix} WebSocket connection error`, err);
        this.socketsStatus.set('offline')
        callbacks.onError?.(err);
      } catch (error) {
        console.error(`${prefix} Error in error callback:`, error);
      }
    });

    pusher.connection.bind('failed', () => {
      try {
        console.warn(`${prefix} WebSocket connection failed`);
        this.socketsStatus.set('offline')
        callbacks.onFailed?.();
      } catch (error) {
        console.error(`${prefix} Error in failed callback:`, error);
      }
    });

    pusher.connection.bind('unavailable', () => {
      try {
        console.warn(`${prefix} WebSocket unavailable`);
        this.socketsStatus.set('offline')
        callbacks.onUnavailable?.();
      } catch (error) {
        console.error(`${prefix} Error in unavailable callback:`, error);
      }
    });
  }

  /**
   * Subscribe to channels with configuration
   */
  subscribeToChannels(channelsConfig: EchoChannelConfig[]): void {
    if (!this.echo) {
      console.warn(`[${this.context}] Echo not initialized`);
      return;
    }

    channelsConfig.forEach((config) => {
      this.subscribeToChannel(config);
    });
  }

  /**
   * Subscribe to a single channel
   */
  private subscribeToChannel(config: EchoChannelConfig): void {
    if (!this.echo) return;

    let channel: any;

    // Join or subscribe to channel based on type
    if (config.type === 'join') {
      channel = this.echo.join(config.channel);
    } else {
      channel = this.echo.channel(config.channel);
    }

    this.channels.set(config.channel, channel);

    // Setup subscribed callback
    channel.subscribed(() => {
      try {
        console.log(`[${this.context}] Successfully subscribed to channel: ${config.channel}`);
        config.onSubscribed?.();
      } catch (error) {
        console.error(`[${this.context}] Error in subscribed callback:`, error);
      }
    });

    // Setup error callback
    channel.error((error: any) => {
      try {
        console.error(`[${this.context}] Failed to subscribe to channel: ${config.channel}`, error);
        config.onError?.(error);
      } catch (err) {
        console.error(`[${this.context}] Error in error callback:`, err);
      }
    });

    // Setup listen events
    if (config.listen) {
      config.listen.forEach((listener) => {
        channel.listen(listener.event, (data: any) => {
          try {
            console.log(`[${this.context}] Event ${listener.event} on channel ${config.channel}`, data);
            listener.callback(data);
          } catch (error) {
            console.error(`[${this.context}] Error in listener callback for ${listener.event}:`, error);
          }
        });
      });
    }

    // Setup whisper events (only for 'join' type channels)
    if (config.type === 'join' && config.listenForWhisper) {
      config.listenForWhisper.forEach((whisper) => {
        channel.listenForWhisper(whisper.event, (data: any) => {
          try {
            console.log(`[${this.context}] Whisper ${whisper.event} on channel ${config.channel}`, data);
            whisper.callback(data);
          } catch (error) {
            console.error(`[${this.context}] Error in whisper callback for ${whisper.event}:`, error);
          }
        });
      });
    }
  }

  /**
   * Get a specific channel by name
   */
  getChannel(channelName: string): any {
    return this.channels.get(channelName);
  }

  /**
   * Disconnect and cleanup
   */
  disconnect(): void {
    if (this.echo) {
      this.echo.disconnect();
      this.channels.clear();
      this.echo = null;
      console.log(`[${this.context}] Echo disconnected`);
    }
  }

  /**
   * Get Echo instance
   */
  getEcho(): Echo<any> | null {
    return this.echo;
  }

  /**
   * Check if Echo is connected
   */
  isConnected(): boolean {
    return this.echo !== null;
  }
}
